import { useState, useEffect } from "react";
import api from "../api";
import PlantillasAlarmas from "./PlantillasAlarmas";
import { useTheme } from "../contexts/ThemeContext";

// Mini-preview de un tema para la tarjeta de selección (colores fijos por tema).
function ThemeMini({ variant }) {
  const c = variant === "color"
    ? { bg:"#f4f7f2", side:"#ffffff", line:"#e6ece1", accent:"#13c313", head:"#14211a", btn:"#13c313" }
    : { bg:"#0b1120", side:"#1e293b", line:"#334155", accent:"#38bdf8", head:"#e2e8f0", btn:"#0ea5e9" };
  return (
    <div style={{ height:96, borderRadius:8, overflow:"hidden", display:"flex", border:"1px solid "+c.line }}>
      <div style={{ width:34, background:c.side, borderRight:"1px solid "+c.line, padding:"8px 6px", display:"flex", flexDirection:"column", gap:5 }}>
        <div style={{ height:6, borderRadius:3, background:c.accent }}/>
        <div style={{ height:6, borderRadius:3, background:c.line }}/>
        <div style={{ height:6, borderRadius:3, background:c.line }}/>
      </div>
      <div style={{ flex:1, padding:9, display:"flex", flexDirection:"column", gap:6, background:c.bg }}>
        <div style={{ height:9, width:"45%", borderRadius:4, background:c.head }}/>
        <div style={{ height:7, width:"70%", borderRadius:4, background:c.line }}/>
        <div style={{ height:7, width:"55%", borderRadius:4, background:c.line }}/>
        <div style={{ height:16, width:58, borderRadius:5, background:c.btn, marginTop:"auto" }}/>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="nv-card" style={{ marginBottom:16 }}>
      <div className="nv-card-header"><span className="nv-card-title">{title}</span></div>
      {children}
    </div>
  );
}

function Alert({ msg, onClose }) {
  if (!msg) return null;
  const cls = {success:"nv-alert-ok",error:"nv-alert-err",warn:"nv-alert-warn"}[msg.type]||"nv-alert-ok";
  return (
    <div className={"nv-alert "+cls} style={{ marginBottom:14 }}>
      <span style={{ flex:1 }}>{msg.text}</span>
      <button onClick={onClose} style={{ background:"none",border:"none",cursor:"pointer",color:"inherit",fontSize:14 }}>✕</button>
    </div>
  );
}


function EyeIcon({ show }) {
  return show ? (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94"/>
      <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19"/>
      <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  ) : (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
      <circle cx="12" cy="12" r="3"/>
    </svg>
  );
}

export default function Settings() {
  const { theme, setTheme } = useTheme();
  const [tab,     setTab]     = useState("password");
  const [msg,     setMsg]     = useState(null);
  const [loading, setLoading] = useState(false);
  const [current, setCurrent] = useState("");
  const [newPwd,  setNewPwd]  = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPwd, setShowPwd] = useState({c:false,n:false,cf:false});
  const [sysInfo,   setSysInfo]   = useState(null);
  const [smtpCfg,   setSmtpCfg]   = useState(null);
  const [showSmtpPwd, setShowSmtpPwd] = useState(false);
  const [smtpForm,  setSmtpForm]  = useState({ host:"", port:"587", user:"", password:"", from_addr:"", from_name:"" });
  const [smtpBusy,  setSmtpBusy]  = useState(false);
  const [smtpTest,  setSmtpTest]  = useState(null);

  const showMsg = (text,type="success") => { setMsg({text,type}); setTimeout(()=>setMsg(null),5000); };

  useEffect(()=>{
    if (tab==="sistema") {
      api.get("/").then(r=>setSysInfo(r.data)).catch(()=>setSysInfo(null));
    }
    if (tab==="smtp" && !smtpCfg) {
      api.get("/config/smtp").then(r=>{ setSmtpCfg(r.data); setSmtpForm({
        host:     r.data.host||"",
        port:     String(r.data.port||"587"),
        user:     r.data.user||"",
        password: "",
        from_addr:r.data.from_addr||"",
        from_name:r.data.from_name||"",
      }); }).catch(e=>setSmtpTest({ok:false,msg:"No se pudo leer la config SMTP: "+(e?.response?.data?.detail||e.message)}));
    }
  },[tab]);

  const handlePassword = async () => {
    if (!current||!newPwd||!confirm) return showMsg("Todos los campos son requeridos","error");
    if (newPwd!==confirm) return showMsg("Las contraseñas no coinciden","error");
    if (newPwd.length<8) return showMsg("Mínimo 8 caracteres","error");
    if (current===newPwd) return showMsg("La nueva debe ser diferente a la actual","error");
    setLoading(true);
    try {
      await api.post("/auth/change-password",{ current_password:current, new_password:newPwd });
      showMsg("Contraseña actualizada correctamente");
      setCurrent(""); setNewPwd(""); setConfirm("");
    } catch(e) {
      showMsg(e?.response?.data?.detail||"Error al cambiar contraseña","error");
    } finally { setLoading(false); }
  };

  const TABS = [
    { key:"apariencia", label:"🎨 Apariencia" },
    { key:"password", label:"⚿ Contraseña" },
    { key:"smtp",     label:"✉ SMTP / Email" },
    { key:"alarmas",  label:"🔔 Mensajes de alarmas" },
    { key:"sistema",  label:"⚙ Sistema" },
    { key:"info",     label:"ℹ Información" },
  ];

  const PWD_FIELDS = [
    { label:"Contraseña actual",  val:current, set:setCurrent, sk:"c"  },
    { label:"Nueva contraseña",   val:newPwd,  set:setNewPwd,  sk:"n"  },
    { label:"Confirmar",           val:confirm, set:setConfirm, sk:"cf" },
  ];

  const STRENGTH = [
    [()=>newPwd.length>=8,    "8+ caracteres"],
    [()=>/[A-Z]/.test(newPwd),"Mayúscula"],
    [()=>/[0-9]/.test(newPwd),"Número"],
    [()=>/[^A-Za-z0-9]/.test(newPwd),"Símbolo"],
  ];

  const SERVICES = [
    { name:"FastAPI Backend", ip:"127.0.0.1:8000", role:"API REST",          ok:!!sysInfo },
    { name:"Soft Switch",   ip:"192.168.182.20",  role:"Soft Switch + Media", ok:true },
    { name:"Kamailio SBC",   ip:"181.39.88.61",   role:"Border Controller",  ok:true },
    { name:"MySQL 8.0",      ip:"127.0.0.1",  role:"Base de datos",      ok:true },
    { name:"Nginx",          ip:"181.39.88.61",    role:"Web / API Gateway",  ok:true },
  ];

  const INFO = [
    ["Plataforma",  "Netvoice Softswitch"],
    ["Version",     "v2.0 Carrier-Class"],
    ["Empresa",     "Linkotel"],
    ["Asterisk",    "20.19.0 PJSIP"],
    ["BD",          "MySQL 8.0"],
    ["Backend",     "FastAPI + Python"],
    ["Frontend",    "React 19 + CRA"],
    ["Web",         "Nginx + HTTPS"],
    ["SBC",         "Kamailio v5.5.4"],
    ["Soft Switch HA",     "Asterisk 20.19.0"],
    ["Panel URL",   (typeof window!=="undefined" && window.location && window.location.host) || "mi.eneural.org"],
    ["ARI",         "127.0.0.1:8088"],
  ];

  return (
    <div>
      <div className="nv-page-header">
        <div>
          <div className="nv-page-title">Configuración</div>
          <div className="nv-page-sub">Administración del sistema</div>
        </div>
      </div>

      <Alert msg={msg} onClose={()=>setMsg(null)}/>

      <div className="nv-tabs">
        {TABS.map(t=>(
          <div key={t.key} className={"nv-tab"+(tab===t.key?" active":"")} onClick={()=>setTab(t.key)}>
            {t.label}
          </div>
        ))}
      </div>

      {tab==="apariencia" && (
        <Section title="🎨 Tema de la interfaz">
          <p style={{ fontSize:12, color:"var(--text-secondary)", margin:"0 0 16px", maxWidth:560 }}>
            Se aplica a todo el panel para tu usuario y queda guardado en este navegador.
            Podés cambiarlo cuando quieras.
          </p>
          <div style={{ display:"flex", gap:16, flexWrap:"wrap" }}>
            {[
              { key:"color", name:"Modo color",  desc:"Claro, en blancos y verdes. Para jornadas con buena luz." },
              { key:"dark",  name:"Modo oscuro", desc:"Azul pizarra y cian. Ideal para NOC y ambientes con poca luz." },
            ].map(opt=>{
              const sel = theme===opt.key;
              return (
                <div key={opt.key} onClick={()=>setTheme(opt.key)} role="button" tabIndex={0}
                  onKeyDown={e=>{ if(e.key==="Enter"||e.key===" ") setTheme(opt.key); }}
                  style={{ cursor:"pointer", width:252, maxWidth:"100%",
                    border:"2px solid "+(sel?"var(--brand)":"var(--border)"),
                    borderRadius:12, padding:14, background:"var(--bg-raised)",
                    boxShadow:sel?"0 0 0 3px var(--brand-subtle)":"none", transition:"all .15s" }}>
                  <ThemeMini variant={opt.key}/>
                  <div style={{ display:"flex", alignItems:"center", gap:8, marginTop:12 }}>
                    <span style={{ width:16, height:16, borderRadius:"50%", flexShrink:0,
                      border:"2px solid "+(sel?"var(--brand)":"var(--text-muted)"),
                      background:sel?"var(--brand)":"transparent",
                      display:"flex", alignItems:"center", justifyContent:"center" }}>
                      {sel && <span style={{ width:6, height:6, borderRadius:"50%", background:"#fff" }}/>}
                    </span>
                    <span style={{ fontSize:13, fontWeight:700, color:"var(--text-primary)" }}>{opt.name}</span>
                    {sel && <span style={{ marginLeft:"auto", fontSize:10, fontWeight:700, color:"var(--success)" }}>✓ Activo</span>}
                  </div>
                  <div style={{ fontSize:11, color:"var(--text-secondary)", marginTop:6, lineHeight:1.5 }}>{opt.desc}</div>
                </div>
              );
            })}
          </div>
        </Section>
      )}

      {tab==="password" && (
        <Section title="⚿ Cambiar contraseña">
          <div style={{ maxWidth:400 }}>
            {PWD_FIELDS.map(({label,val,set,sk})=>(
              <div key={sk} className="nv-form-field" style={{ marginBottom:12 }}>
                <label className="nv-label">{label}</label>
                <div style={{ position:"relative" }}>
                  <input className="nv-input" type={showPwd[sk]?"text":"password"}
                    value={val} onChange={e=>set(e.target.value)}
                    placeholder="••••••••"
                    style={{ paddingRight:36 }}/>
                  <button type="button"
                    onClick={()=>setShowPwd(p=>({...p,[sk]:!p[sk]}))}
                    style={{ position:"absolute",right:8,top:"50%",transform:"translateY(-50%)",
                      background:"none",border:"none",color:"var(--text-muted)",cursor:"pointer",fontSize:13 }}>
                    {showPwd[sk]?"○":"●"}
                  </button>
                </div>
              </div>
            ))}
            {newPwd && (
              <div style={{ display:"flex", gap:10, flexWrap:"wrap", marginBottom:14 }}>
                {STRENGTH.map(([check,lbl])=>{
                  const ok = check();
                  return (
                    <span key={lbl} style={{ fontSize:10, color:ok?"var(--success)":"var(--danger)" }}>
                      {ok?"✓":"○"} {lbl}
                    </span>
                  );
                })}
              </div>
            )}
            <button className="nv-btn nv-btn-primary" onClick={handlePassword} disabled={loading}>
              {loading?<span className="nv-spinner"/>:"✓ Actualizar contraseña"}
            </button>
          </div>
        </Section>
      )}

      {tab==="smtp" && (
        <Section title="✉ Configuración SMTP">
          {smtpCfg && (
            <div className={"nv-alert "+(smtpCfg.configured?"nv-alert-ok":"nv-alert-warn")} style={{ marginBottom:16 }}>
              {!smtpCfg.configured ? "SMTP no configurado — los correos no salen (OTP solo va al log del servidor)"
                : smtpCfg.source==="db" ? `SMTP activo — cuenta guardada en el panel${smtpCfg.updated_by?` (por ${smtpCfg.updated_by})`:""}`
                : "SMTP activo — tomado del .env del servidor (respaldo). Al guardar acá pasa a usarse la del panel."}
              {smtpCfg.db_incompleta && <div style={{ marginTop:6 }}>⚠ Hay una config guardada en el panel que no se puede usar (password ilegible); se está usando el .env. Volvé a guardar con la password.</div>}
            </div>
          )}
          <div style={{ maxWidth:480 }}>
            <div className="nv-form-row">
              <div className="nv-form-field">
                <label className="nv-label">Servidor SMTP</label>
                <input className="nv-input" value={smtpForm.host} onChange={e=>setSmtpForm(f=>({...f,host:e.target.value}))} placeholder="smtp.gmail.com"/>
              </div>
              <div className="nv-form-field">
                <label className="nv-label">Puerto</label>
                <input className="nv-input" value={smtpForm.port} onChange={e=>setSmtpForm(f=>({...f,port:e.target.value}))} placeholder="587"/>
              </div>
            </div>
            <div className="nv-form-row">
              <div className="nv-form-field">
                <label className="nv-label">Usuario</label>
                <input className="nv-input" value={smtpForm.user} onChange={e=>setSmtpForm(f=>({...f,user:e.target.value}))} placeholder="usuario@gmail.com"/>
              </div>
              <div className="nv-form-field">
                <label className="nv-label">Password / App Password</label>
                <div style={{ position:"relative",display:"flex",alignItems:"center" }}>
                <input className="nv-input" type={showSmtpPwd?"text":"password"} value={smtpForm.password} onChange={e=>setSmtpForm(f=>({...f,password:e.target.value}))} placeholder={smtpCfg?.password_set?"•••••••• (guardada — vacío = no cambiarla)":"Password de la cuenta"} style={{ paddingRight:36 }}/>
                <button type="button" style={{ position:"absolute",right:10,background:"none",border:"none",cursor:"pointer",color:"var(--text-muted)",display:"flex",alignItems:"center",padding:0 }} onClick={()=>setShowSmtpPwd(v=>!v)}><EyeIcon show={showSmtpPwd}/></button>
              </div>
              </div>
            </div>
            <div className="nv-form-row">
              <div className="nv-form-field">
                <label className="nv-label">Email remitente</label>
                <input className="nv-input" value={smtpForm.from_addr} onChange={e=>setSmtpForm(f=>({...f,from_addr:e.target.value}))} placeholder="noreply@linkotel.com"/>
              </div>
              <div className="nv-form-field">
                <label className="nv-label">Nombre remitente</label>
                <input className="nv-input" value={smtpForm.from_name} onChange={e=>setSmtpForm(f=>({...f,from_name:e.target.value}))} placeholder="Netvoice / Linkotel"/>
              </div>
            </div>
            {smtpTest && (
              <div className={"nv-alert "+(smtpTest.ok?"nv-alert-ok":"nv-alert-err")} style={{ marginBottom:14 }}>
                {smtpTest.msg}
              </div>
            )}
            <div style={{ display:"flex",gap:8,marginTop:4 }}>
              <button className="nv-btn nv-btn-ghost" disabled={smtpBusy}
                onClick={async()=>{
                  setSmtpBusy(true); setSmtpTest(null);
                  try {
                    const r = await api.post("/config/smtp/test", smtpForm);
                    setSmtpTest({ok:true,msg:r.data.message});
                  } catch(e) { setSmtpTest({ok:false,msg:e?.response?.data?.detail||"Error de conexion"}); }
                  finally { setSmtpBusy(false); }
                }}>
                {smtpBusy?<span className="nv-spinner"/>:"▶ Probar conexion"}
              </button>
              <button className="nv-btn nv-btn-primary" disabled={smtpBusy}
                onClick={async()=>{
                  setSmtpBusy(true);
                  try {
                    await api.put("/config/smtp", smtpForm);
                    showMsg("Configuracion SMTP guardada");
                    const r = await api.get("/config/smtp"); setSmtpCfg(r.data);
                    setSmtpForm(f=>({...f,password:""}));
                  } catch(e) { showMsg(e?.response?.data?.detail||"Error al guardar","error"); }
                  finally { setSmtpBusy(false); }
                }}>
                {smtpBusy?<span className="nv-spinner"/>:"✓ Guardar SMTP"}
              </button>
            </div>
            <div style={{ marginTop:16,padding:"12px 14px",background:"var(--bg-raised)",borderRadius:"var(--r-sm)",fontSize:11,color:"var(--text-muted)",lineHeight:1.7 }}>
              <strong style={{ color:"var(--text-secondary)" }}>Gmail:</strong> Activa la verificacion en 2 pasos y genera un App Password en myaccount.google.com/apppasswords<br/>
              <strong style={{ color:"var(--text-secondary)" }}>Otros:</strong> Usa las credenciales SMTP de tu proveedor de email corporativo.
            </div>
          </div>
        </Section>
      )}
      {tab==="alarmas" && <PlantillasAlarmas onMsg={setMsg}/>}

      {tab==="sistema" && (
        <div>
          <Section title="◉ Estado de servicios">
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))", gap:10 }}>
              {SERVICES.map(n=>(
                <div key={n.name} style={{ display:"flex",alignItems:"center",gap:10,
                  padding:"10px 12px",background:"var(--bg-raised)",
                  borderRadius:"var(--r-sm)",border:"1px solid var(--border)" }}>
                  <div style={{ width:8,height:8,borderRadius:"50%",flexShrink:0,
                    background:n.ok?"var(--success)":"var(--danger)",
                    boxShadow:n.ok?"0 0 6px var(--success)":"none" }}/>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:12,fontWeight:600,color:"var(--text-primary)" }}>{n.name}</div>
                    <div style={{ fontSize:10,color:"var(--text-muted)",fontFamily:"var(--font-mono)" }}>{n.ip}</div>
                  </div>
                  <div style={{ textAlign:"right" }}>
                    <div style={{ fontSize:10,fontWeight:700,color:n.ok?"var(--success)":"var(--danger)" }}>
                      {n.ok?"Online":"Offline"}
                    </div>
                    <div style={{ fontSize:9,color:"var(--text-muted)" }}>{n.role}</div>
                  </div>
                </div>
              ))}
            </div>
          </Section>
          {sysInfo && (
            <Section title="▦ API Info">
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
                {Object.entries(sysInfo).map(([k,v])=>(
                  <div key={k} style={{ padding:"8px 10px",background:"var(--bg-raised)",borderRadius:"var(--r-sm)" }}>
                    <div style={{ fontSize:9,textTransform:"uppercase",letterSpacing:".07em",color:"var(--text-muted)",marginBottom:2 }}>{k}</div>
                    <div style={{ fontSize:12,fontWeight:600,color:"var(--text-primary)",fontFamily:"var(--font-mono)" }}>{String(v)}</div>
                  </div>
                ))}
              </div>
            </Section>
          )}
        </div>
      )}

      {tab==="info" && (
        <Section title="ℹ Stack técnico">
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
            {INFO.map(([k,v])=>(
              <div key={k} style={{ padding:"8px 10px",background:"var(--bg-raised)",borderRadius:"var(--r-sm)" }}>
                <div style={{ fontSize:9,textTransform:"uppercase",letterSpacing:".07em",color:"var(--text-muted)",marginBottom:2 }}>{k}</div>
                <div style={{ fontSize:12,fontWeight:600,color:"var(--text-primary)",fontFamily:"var(--font-mono)" }}>{v}</div>
              </div>
            ))}
          </div>
        </Section>
      )}
    </div>
  );
}
